import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    Grid,
    IconButton,
    Chip,
    Alert,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tooltip,
    Avatar,
    Container,
    useTheme,
    alpha,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    Link,
} from '@mui/material';
import {
    PlayArrow,
    Stop,
    Refresh as RefreshIcon,
    Close as CloseIcon,
    Info as InfoIcon,
    OpenInNew,
    CloudQueue,
    Storage,
    CheckCircle,
    Error as ErrorIcon,
    Schedule,
    Build,
    ContentCopy,
    Terminal,
    Visibility,
    RestartAlt,
} from '@mui/icons-material';
import { observer } from 'mobx-react-lite';
import { useStore } from '../store';
import { Deployment, DeploymentStatus, DeploymentLog } from '../types/deployment';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';

const Deployments: React.FC = observer(() => {
    const { deploymentStore, applicationStore } = useStore();
    const theme = useTheme();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [selectedDeployment, setSelectedDeployment] = useState<Deployment | null>(null);
    const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
    const [openLogsDialog, setOpenLogsDialog] = useState(false);
    const [deploymentLogs, setDeploymentLogs] = useState<DeploymentLog[]>([]);
    const [openCreateDialog, setOpenCreateDialog] = useState(false);
    const [selectedApplication, setSelectedApplication] = useState<number | null>(null);
    const [selectedTemplate, setSelectedTemplate] = useState<number | null>(null);

    useEffect(() => {
        loadData();
        const interval = setInterval(loadData, 10000); // Обновляем каждые 10 секунд
        return () => clearInterval(interval);
    }, [page, rowsPerPage, statusFilter]);

    const loadData = () => {
        deploymentStore.fetchDeployments(page + 1, rowsPerPage, statusFilter);
        deploymentStore.fetchTemplates(true); // Только активные шаблоны
        deploymentStore.fetchDeploymentStats();
        applicationStore.fetchApplications(1, 100); // Загружаем заявки для выбора
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleViewDetails = async (deployment: Deployment) => {
        const fullDeployment = await deploymentStore.getDeployment(deployment.id);
        if (fullDeployment) {
            setSelectedDeployment(fullDeployment);
            setOpenDetailsDialog(true);
        }
    };

    const handleViewLogs = async (deployment: Deployment) => {
        await deploymentStore.fetchDeploymentLogs(deployment.id);
        setDeploymentLogs(deploymentStore.logs);
        setSelectedDeployment(deployment);
        setOpenLogsDialog(true);
    };

    const handleStop = async (deployment: Deployment) => {
        if (window.confirm('Вы уверены, что хотите остановить это развертывание?')) {
            await deploymentStore.stopDeployment(deployment.id);
        }
    };

    const handleRestart = async (deployment: Deployment) => {
        if (window.confirm('Вы уверены, что хотите перезапустить это развертывание?')) {
            await deploymentStore.restartDeployment(deployment.id);
        }
    };

    const handleCreateDeployment = async () => {
        if (selectedApplication && selectedTemplate) {
            const result = await deploymentStore.createDeployment({
                application_id: selectedApplication,
                template_id: selectedTemplate,
            });
            if (result) {
                setOpenCreateDialog(false);
                setSelectedApplication(null);
                setSelectedTemplate(null);
            }
        }
    };

    const getStatusSteps = (status: DeploymentStatus): number => {
        const steps: DeploymentStatus[] = [
            'build_image',
            'deploy_db',
            'insert_dump_db',
            'deploy_container',
            'config_container',
            'completed'
        ];
        const index = steps.indexOf(status);
        return index >= 0 ? index : 0;
    };

    const getStatusColor = (status: DeploymentStatus) => {
        return deploymentStore.getStatusColor(status);
    };

    const getStatusIcon = (status: DeploymentStatus) => {
        switch (status) {
            case 'build_image':
                return <Build />;
            case 'deploy_db':
            case 'insert_dump_db':
                return <Storage />;
            case 'deploy_container':
            case 'config_container':
                return <CloudQueue />;
            case 'completed':
                return <CheckCircle />;
            case 'failed':
                return <ErrorIcon />;
            default:
                return <Schedule />;
        }
    };

    const formatDate = (dateString: string) => {
        return format(new Date(dateString), 'dd.MM.yyyy HH:mm', { locale: ru });
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
    };

    const MetricCard = ({ title, value, icon, color = 'primary' }: {
        title: string;
        value: number;
        icon: React.ReactNode;
        color?: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info';
    }) => (
        <Paper
            sx={{
                p: 3,
                textAlign: 'center',
                background: `linear-gradient(135deg, ${alpha(theme.palette[color].main, 0.1)} 0%, ${alpha(theme.palette[color].main, 0.05)} 100%)`,
                border: `1px solid ${alpha(theme.palette[color].main, 0.2)}`,
            }}
        >
            <Avatar
                sx={{
                    bgcolor: alpha(theme.palette[color].main, 0.1),
                    color: theme.palette[color].main,
                    width: 56,
                    height: 56,
                    mx: 'auto',
                    mb: 2,
                }}
            >
                {icon}
            </Avatar>
            <Typography variant="h4" fontWeight={700} color={`${color}.main`}>
                {value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                {title}
            </Typography>
        </Paper>
    );

    return (
        <Container maxWidth="xl">
            <Box mb={4}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Box>
                        <Typography variant="h3" gutterBottom fontWeight="bold" color="primary">
                            Развертывания
                        </Typography>
                        <Typography variant="h6" color="textSecondary">
                            Управление развернутыми приложениями
                        </Typography>
                    </Box>
                    <Box display="flex" gap={2}>
                        <Button
                            variant="contained"
                            startIcon={<PlayArrow />}
                            onClick={() => setOpenCreateDialog(true)}
                            size="large"
                        >
                            Новое развертывание
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={loadData}
                        >
                            Обновить
                        </Button>
                    </Box>
                </Box>

                {deploymentStore.error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {deploymentStore.error}
                    </Alert>
                )}

                {/* Статистика */}
                {deploymentStore.stats && (
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <MetricCard
                                title="Всего развертываний"
                                value={deploymentStore.stats.total_deployments}
                                icon={<CloudQueue />}
                                color="primary"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <MetricCard
                                title="Активных"
                                value={deploymentStore.stats.active_deployments}
                                icon={<CheckCircle />}
                                color="success"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <MetricCard
                                title="С ошибками"
                                value={deploymentStore.stats.failed_deployments}
                                icon={<ErrorIcon />}
                                color="error"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <MetricCard
                                title="Среднее время (сек)"
                                value={deploymentStore.stats.average_deployment_time}
                                icon={<Schedule />}
                                color="info"
                            />
                        </Grid>
                    </Grid>
                )}

                {/* Фильтры */}
                <Paper sx={{ p: 2, mb: 3 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={4} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Статус</InputLabel>
                                <Select
                                    value={statusFilter}
                                    label="Статус"
                                    onChange={(e) => {
                                        setStatusFilter(e.target.value);
                                        setPage(0);
                                    }}
                                >
                                    <MenuItem value="">Все</MenuItem>
                                    <MenuItem value="build_image">Сборка образа</MenuItem>
                                    <MenuItem value="deploy_db">Создание БД</MenuItem>
                                    <MenuItem value="insert_dump_db">Загрузка данных</MenuItem>
                                    <MenuItem value="deploy_container">Развертывание</MenuItem>
                                    <MenuItem value="config_container">Настройка</MenuItem>
                                    <MenuItem value="completed">Завершено</MenuItem>
                                    <MenuItem value="failed">Ошибка</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Таблица развертываний */}
                <Paper>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'grey.50' }}>
                                    <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Заявка</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Шаблон</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Статус</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>URL доступа</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Создано</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Действия</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {deploymentStore.loading ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                                            <CircularProgress size={40} />
                                            <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                                                Загрузка развертываний...
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : deploymentStore.deployments.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                                            <CloudQueue sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                                            <Typography variant="h6" color="textSecondary">
                                                Нет развертываний
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                Создайте первое развертывание для приложения
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    deploymentStore.deployments.map((deployment) => (
                                        <TableRow
                                            key={deployment.id}
                                            hover
                                            sx={{
                                                '&:hover': {
                                                    bgcolor: 'action.hover',
                                                },
                                            }}
                                        >
                                            <TableCell>
                                                <Typography variant="body2" fontWeight="bold">
                                                    #{deployment.id}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {deployment.unique_identifier}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {deployment.application ? (
                                                    <Box>
                                                        <Typography variant="body2">
                                                            {deployment.application.full_name}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {deployment.application.company}
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                        —
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {deployment.template ? (
                                                    <Chip
                                                        label={deployment.template.name}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                        —
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    {getStatusIcon(deployment.status)}
                                                    <Chip
                                                        label={deploymentStore.getStatusText(deployment.status)}
                                                        color={getStatusColor(deployment.status) as any}
                                                        size="small"
                                                    />
                                                </Box>
                                                {deployment.status === 'failed' && deployment.error_message && (
                                                    <Tooltip title={deployment.error_message}>
                                                        <InfoIcon fontSize="small" color="error" sx={{ ml: 0.5 }} />
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {deployment.access_url ? (
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <Link
                                                            href={deployment.access_url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            sx={{ textDecoration: 'none' }}
                                                        >
                                                            {deployment.subdomain}
                                                        </Link>
                                                        <Tooltip title="Открыть в новом окне">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => window.open(deployment.access_url, '_blank')}
                                                            >
                                                                <OpenInNew fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Скопировать URL">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => copyToClipboard(deployment.access_url)}
                                                            >
                                                                <ContentCopy fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                        —
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {formatDate(deployment.created_at)}
                                                </Typography>
                                                {deployment.completed_at && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        Завершено: {formatDate(deployment.completed_at)}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Box display="flex" gap={0.5}>
                                                    <Tooltip title="Подробности">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleViewDetails(deployment)}
                                                        >
                                                            <Visibility fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Логи">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleViewLogs(deployment)}
                                                        >
                                                            <Terminal fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    {deployment.status === 'completed' && (
                                                        <Tooltip title="Перезапустить">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleRestart(deployment)}
                                                                color="primary"
                                                            >
                                                                <RestartAlt fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {(deployment.status === 'completed' || deployment.status === 'failed') && (
                                                        <Tooltip title="Остановить">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleStop(deployment)}
                                                                color="error"
                                                            >
                                                                <Stop fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        component="div"
                        count={deploymentStore.total}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelRowsPerPage="Строк на странице:"
                        labelDisplayedRows={({ from, to, count }) =>
                            `${from}-${to} из ${count !== -1 ? count : `более чем ${to}`}`
                        }
                    />
                </Paper>
            </Box>

            {/* Диалог создания развертывания */}
            <Dialog
                open={openCreateDialog}
                onClose={() => setOpenCreateDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">Новое развертывание</Typography>
                        <IconButton onClick={() => setOpenCreateDialog(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <FormControl fullWidth required>
                                <InputLabel>Заявка</InputLabel>
                                <Select
                                    value={selectedApplication || ''}
                                    onChange={(e) => setSelectedApplication(Number(e.target.value))}
                                    label="Заявка"
                                >
                                    {applicationStore.applications.map((app) => (
                                        <MenuItem key={app.id} value={app.id}>
                                            <Box display="flex" justifyContent="space-between" width="100%">
                                                <span>{app.full_name} ({app.company})</span>
                                                <Chip
                                                    label={app.tariff}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth required>
                                <InputLabel>Шаблон развертывания</InputLabel>
                                <Select
                                    value={selectedTemplate || ''}
                                    onChange={(e) => setSelectedTemplate(Number(e.target.value))}
                                    label="Шаблон развертывания"
                                >
                                    {deploymentStore.activeTemplates.map((template) => (
                                        <MenuItem key={template.id} value={template.id}>
                                            <Box>
                                                <Typography variant="body2">{template.name}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {template.description}
                                                </Typography>
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    <Alert severity="info" sx={{ mt: 2 }}>
                        Развертывание создаст новый экземпляр приложения с отдельной базой данных
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenCreateDialog(false)}>Отмена</Button>
                    <Button
                        variant="contained"
                        onClick={handleCreateDeployment}
                        disabled={!selectedApplication || !selectedTemplate || deploymentStore.saving}
                        startIcon={deploymentStore.saving ? <CircularProgress size={20} /> : <PlayArrow />}
                    >
                        {deploymentStore.saving ? 'Создание...' : 'Создать'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Диалог подробностей */}
            <Dialog
                open={openDetailsDialog}
                onClose={() => setOpenDetailsDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">Детали развертывания</Typography>
                        <IconButton onClick={() => setOpenDetailsDialog(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {selectedDeployment && (
                        <Grid container spacing={3}>
                            {/* Основная информация */}
                            <Grid item xs={12}>
                                <Paper sx={{ p: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Основная информация
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">ID</Typography>
                                            <Typography variant="body2">#{selectedDeployment.id}</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">Уникальный идентификатор</Typography>
                                            <Typography variant="body2">{selectedDeployment.unique_identifier}</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">Статус</Typography>
                                            <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                                                {getStatusIcon(selectedDeployment.status)}
                                                <Chip
                                                    label={deploymentStore.getStatusText(selectedDeployment.status)}
                                                    color={getStatusColor(selectedDeployment.status) as any}
                                                    size="small"
                                                />
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">Шаблон</Typography>
                                            <Typography variant="body2">
                                                {selectedDeployment.template?.name || '—'}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Grid>

                            {/* Прогресс развертывания */}
                            <Grid item xs={12}>
                                <Paper sx={{ p: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Прогресс развертывания
                                    </Typography>
                                    <Stepper activeStep={getStatusSteps(selectedDeployment.status)} orientation="vertical">
                                        <Step completed={getStatusSteps(selectedDeployment.status) > 0}>
                                            <StepLabel>Сборка Docker образа</StepLabel>
                                            <StepContent>
                                                <Typography variant="caption">
                                                    Сборка образа из шаблона {selectedDeployment.template?.name}
                                                </Typography>
                                            </StepContent>
                                        </Step>
                                        <Step completed={getStatusSteps(selectedDeployment.status) > 1}>
                                            <StepLabel>Создание базы данных</StepLabel>
                                            <StepContent>
                                                <Typography variant="caption">
                                                    База данных: {selectedDeployment.database_name}
                                                </Typography>
                                            </StepContent>
                                        </Step>
                                        <Step completed={getStatusSteps(selectedDeployment.status) > 2}>
                                            <StepLabel>Загрузка данных</StepLabel>
                                            <StepContent>
                                                <Typography variant="caption">
                                                    Импорт начальных данных в базу
                                                </Typography>
                                            </StepContent>
                                        </Step>
                                        <Step completed={getStatusSteps(selectedDeployment.status) > 3}>
                                            <StepLabel>Развертывание контейнера</StepLabel>
                                            <StepContent>
                                                <Typography variant="caption">
                                                    Контейнер: {selectedDeployment.container_name}
                                                </Typography>
                                            </StepContent>
                                        </Step>
                                        <Step completed={getStatusSteps(selectedDeployment.status) > 4}>
                                            <StepLabel>Настройка приложения</StepLabel>
                                            <StepContent>
                                                <Typography variant="caption">
                                                    Финальная настройка и проверка
                                                </Typography>
                                            </StepContent>
                                        </Step>
                                    </Stepper>
                                </Paper>
                            </Grid>

                            {/* Технические детали */}
                            <Grid item xs={12}>
                                <Paper sx={{ p: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Технические детали
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <Typography variant="caption" color="text.secondary">URL доступа</Typography>
                                            {selectedDeployment.access_url ? (
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Link href={selectedDeployment.access_url} target="_blank">
                                                        {selectedDeployment.access_url}
                                                    </Link>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => copyToClipboard(selectedDeployment.access_url)}
                                                    >
                                                        <ContentCopy fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            ) : (
                                                <Typography variant="body2">—</Typography>
                                            )}
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">Docker хост</Typography>
                                            <Typography variant="body2">{selectedDeployment.docker_host}</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">Образ</Typography>
                                            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                                {selectedDeployment.container_image}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">База данных</Typography>
                                            <Typography variant="body2">
                                                {selectedDeployment.database_name}@{selectedDeployment.database_host}:{selectedDeployment.database_port}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">Порт приложения</Typography>
                                            <Typography variant="body2">{selectedDeployment.container_port}</Typography>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Grid>

                            {selectedDeployment.error_message && (
                                <Grid item xs={12}>
                                    <Alert severity="error">
                                        <Typography variant="subtitle2" gutterBottom>Ошибка развертывания</Typography>
                                        <Typography variant="body2">{selectedDeployment.error_message}</Typography>
                                    </Alert>
                                </Grid>
                            )}
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDetailsDialog(false)}>Закрыть</Button>
                </DialogActions>
            </Dialog>

            {/* Диалог логов */}
            <Dialog
                open={openLogsDialog}
                onClose={() => setOpenLogsDialog(false)}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">Логи развертывания</Typography>
                        <IconButton onClick={() => setOpenLogsDialog(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <List>
                        {deploymentLogs.length === 0 ? (
                            <ListItem>
                                <ListItemText
                                    primary="Нет логов"
                                    secondary="Логи развертывания пока отсутствуют"
                                />
                            </ListItem>
                        ) : (
                            deploymentLogs.map((log) => (
                                <ListItem key={log.id} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                                    <ListItemIcon>
                                        {getStatusIcon(log.status)}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <Box display="flex" justifyContent="space-between">
                                                <Typography variant="body2">{log.message}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {formatDate(log.created_at)}
                                                </Typography>
                                            </Box>
                                        }
                                        secondary={
                                            log.details && (
                                                <Typography
                                                    variant="caption"
                                                    component="pre"
                                                    sx={{
                                                        fontFamily: 'monospace',
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-all',
                                                    }}
                                                >
                                                    {log.details}
                                                </Typography>
                                            )
                                        }
                                    />
                                </ListItem>
                            ))
                        )}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenLogsDialog(false)}>Закрыть</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
});

export default Deployments;